import React from "react";
import { useApp } from "../context/AppContext";
import { getCategoryData, getMonthlyData, CATEGORY_COLORS } from "../data/mockData";
import { getSummary, formatCurrency } from "../utils/helpers";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";


function InsightCard({ icon, title, value, subtitle, color, theme }) {
  const dark = theme === "dark";
  return (
    <div style={{
      background: dark ? "rgba(255,255,255,0.04)" : "#fff",
      border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
      borderRadius: 16, padding: "20px 24px",
      boxShadow: dark ? "0 4px 24px rgba(0,0,0,0.2)" :
       "0 4px 20px rgba(0,0,0,0.06)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: `${color}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{icon}</div>
        <span style={{ color: dark ? "#64748b" : 
          "#94a3b8", fontSize: 13, fontWeight: 500 }}>{title}</span>
      </div>
      <p style={{ margin: "0 0 4px", fontSize: 26, 
        fontWeight: 700, color: color || (dark ? "#f1f5f9" : "#0f172a") }}>{value}</p>
      {subtitle && <p style={{ margin: 0, fontSize: 13, color: dark ? "#475569" : "#94a3b8" }}>{subtitle}</p>}
    </div>
  );
}

export default function Insights() {
  const { state } = useApp();
  const { transactions, theme } = state;
  const dark = theme === "dark";
  const { income, expense, balance } = getSummary(transactions);
  const catData = getCategoryData(transactions);
  const monthlyData = getMonthlyData(transactions);
  const topCat = catData[0] || {};
  const savingsRate = income > 0 ? ((balance / income) * 100).toFixed(1) : 0;

  const monthCompare = monthlyData.map((m) => ({ ...m, savings: m.income - m.expense }));

  const getFinancialTip = () => {
    const rate = parseFloat(savingsRate);
    if (rate >= 30) return { tip: "Excellent savings rate! You're building strong financial security.", icon: "🏆", color: "#10b981" };
    if (rate >= 20) return { tip: "Great job! You're on track. Consider investing your surplus.", icon: "✅", color: "#6366f1" };
    if (rate >= 10) return { tip: "Good start. Try to cut back on your top spending category to boost savings.", icon: "📈", color: "#f59e0b" };
    return { tip: `Expenses are high. Your top spend is ${topCat.name} at ${formatCurrency(topCat.value)}. Look for cuts here.`, icon: "⚠️", color: "#f43f5e" };
  };

  const tip = getFinancialTip();
  const textColor = dark ? "#475569" : "#94a3b8";
  const gridColor = dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.06)";

  return (
    <div style={{ padding: 24, maxWidth: 1400 }}>
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ margin: "0 0 4px", color: dark ? 
          "#f1f5f9" : "#0f172a", fontSize: 20, fontWeight: 700 }}>Financial Insights</h2>
        <p style={{ margin: 0, color: dark ? "#475569" : "#94a3b8", fontSize: 14 }}>AI-powered analysis of your spending patterns</p>
      </div>

      {/* Smart Tip */}
      <div style={{
        background: `linear-gradient(135deg, ${tip.color}18, ${tip.color}08)`,
        border: `1px solid ${tip.color}30`,
        borderRadius: 16, padding: "20px 24px", marginBottom: 28,
        display: "flex", alignItems: "flex-start", gap: 16,
      }}>
        <span style={{ fontSize: 32, flexShrink: 0 }}>{tip.icon}</span>
        <div>
          <h3 style={{ margin: "0 0 6px", color: tip.color, fontSize: 16, fontWeight: 700 }}>Financial Insight</h3>
          <p style={{ margin: 0, color: dark ? "#cbd5e1" 
            : "#334155", fontSize: 14, lineHeight: 1.6 }}>{tip.tip}</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 28 }}>
        <InsightCard icon="$" title="Total Savings" value={formatCurrency(balance)} subtitle="Income minus expenses" color="#6366f1" theme={theme} />
        <InsightCard icon="S" title="Savings Rate" value={`${savingsRate}%`} subtitle="Of total income" color={parseFloat(savingsRate) >= 20 ? "#10b981" : "#f59e0b"} theme={theme} />
        <InsightCard icon="-" title="Top Spending" value={topCat.name || "N/A"} subtitle={topCat.value ? formatCurrency(topCat.value) : ""} color={CATEGORY_COLORS[topCat.name] || "#f43f5e"} theme={theme} />
        <InsightCard icon="+" title="Avg Monthly Savings" value={formatCurrency(balance / (monthlyData.length || 1))} subtitle="Per month" color="#10b981" theme={theme} />
      </div>

      {/* Charts */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: 20, marginBottom: 28 }}>
        {/* Monthly comparison */}
        <div style={{ background: dark ? "rgba(255,255,255,0.04)" : "#fff", border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`, borderRadius: 16, padding: 24 }}>
          <h3 style={{ margin: "0 0 4px", color: dark ? "#f1f5f9" : "#0f172a", fontSize: 16, fontWeight: 700 }}>Monthly Income vs Expense</h3>
          <p style={{ margin: "0 0 20px", color: textColor, fontSize: 13 }}>Side-by-side comparison</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={monthlyData} margin={{ top: 5,
               right: 5, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="month" stroke={textColor} tick={{ fill: textColor, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis stroke={textColor} tick={{ fill: textColor,
                 fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ background: dark ? "#1e2a3a" : "#fff", border: "none", borderRadius: 8 }} />
              <Bar dataKey="income" fill="#10b981" name="Income" radius={[4, 4, 0, 0]} maxBarSize={30} />
              <Bar dataKey="expense" fill="#f43f5e" name="Expense" radius={[4, 4, 0, 0]} maxBarSize={30} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Spending by category bar */}
        <div style={{ background: dark ? "rgba(255,255,255,0.04)" : "#fff", border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`, borderRadius: 16, padding: 24 }}>
          <h3 style={{ margin: "0 0 4px", color: dark ? "#f1f5f9" : "#0f172a", fontSize: 16, fontWeight: 700 }}>Spending by Category</h3>
          <p style={{ margin: "0 0 20px", color: textColor, fontSize: 13 }}>Total expenses per category</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={catData} layout="vertical" margin={{ top: 5, right: 30, left: 60, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} horizontal={false} />
              <XAxis type="number" stroke={textColor} 
              tick={{ fill: textColor, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
              <YAxis type="category" dataKey="name" 
              stroke={textColor} tick={{ fill: textColor, fontSize: 12 }} axisLine={false} tickLine={false} width={55} />
              <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ background: dark ? "#1e2a3a" : "#fff", border: "none", borderRadius: 8 }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={24}>

                {catData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly savings trend table */}
      <div style={{ background: dark ? "rgba(255,255,255,0.04)" : "#fff", border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`, borderRadius: 16, padding: 24 }}>
        <h3 style={{ margin: "0 0 20px", color: dark ? "#f1f5f9" : "#0f172a", fontSize: 16, fontWeight: 700 }}>Monthly Breakdown</h3>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Month", "Income", "Expenses", "Savings", "Rate"].map((h) => (
                  <th key={h} style={{ padding: "10px 16px", textAlign: h === "Month" ? "left" : "right", color: textColor, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.08)"}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {monthCompare.map((m) => {
                const rate = m.income > 0 ? ((m.savings / m.income) * 100).toFixed(0) : 0;
                return (
                  <tr key={m.month} style={{ borderBottom:
                   `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.05)"}` }}>
                    <td style={{ padding: "12px 16px", 
                      color: dark ? "#e2e8f0" : "#0f172a", fontWeight: 600 }}>{m.month}</td>
                    <td style={{ padding: "12px 16px", textAlign:
                       "right", color: "#10b981", fontWeight: 600 }}>{formatCurrency(m.income)}</td>
                    <td style={{ padding: "12px 16px", textAlign: "right", color: "#f43f5e", fontWeight: 600 }}>{formatCurrency(m.expense)}</td>
                    <td style={{ padding: "12px 16px", textAlign: "right", color: m.savings >= 0 ? "#6366f1" : "#f43f5e", fontWeight: 700 }}>{formatCurrency(m.savings)}</td>
                    <td style={{ padding: "12px 16px", textAlign: "right" }}>
                      <span style={{ padding: "3px 8px", borderRadius: 12, fontSize: 12, fontWeight: 600, background: rate >= 20 ? "rgba(16,185,129,0.15)" : rate >= 10 ? "rgba(245,158,11,0.15)" : "rgba(244,63,94,0.15)", color: rate >= 20 ? "#10b981" : rate >= 10 ? "#f59e0b" : "#f43f5e" }}>{rate}%</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
